import type { AnalysisData, JsonObject } from "../types";
import { workspaceStorage } from "./storage";

export interface LiveSettings {
  endpoint: string;
  apiKey: string;
}

export interface AggregateSnapshot {
  host: string;
  observed_at: string;
  received_at: string;
  window_count: number;
  open_alerts: number;
  critical_alerts: number;
  readiness_state: string;
  coverage_ratio: number;
}

function hex(value: ArrayBuffer): string {
  return [...new Uint8Array(value)].map((item) => item.toString(16).padStart(2, "0")).join("");
}

export async function pseudonymousHost(host: string): Promise<string> {
  let salt: string;
  try {
    salt = await workspaceStorage.identitySalt();
  } catch {
    const key = "hostwatch-session-identity";
    salt = sessionStorage.getItem(key) ?? crypto.randomUUID().replaceAll("-", "");
    sessionStorage.setItem(key, salt);
  }
  const data = new TextEncoder().encode(`${salt}\0${host}`);
  return `host-${hex(await crypto.subtle.digest("SHA-256", data)).slice(0, 16)}`;
}

export async function signedHeaders(
  key: string,
  body: string,
  agentId: string,
  timestamp = Math.floor(Date.now() / 1000).toString(),
  nonce = crypto.randomUUID(),
): Promise<Record<string, string>> {
  const material = await crypto.subtle.importKey(
    "raw", new TextEncoder().encode(key), { name: "HMAC", hash: "SHA-256" }, false, ["sign"],
  );
  const signature = await crypto.subtle.sign(
    "HMAC", material, new TextEncoder().encode(`${agentId}.${timestamp}.${nonce}.${body}`),
  );
  return {
    "x-hostwatch-agent": agentId,
    "x-hostwatch-timestamp": timestamp,
    "x-hostwatch-nonce": nonce,
    "x-hostwatch-signature": hex(signature),
  };
}

export async function pushSnapshot(data: AnalysisData, settings: LiveSettings): Promise<string> {
  const endpoint = settings.endpoint.replace(/\/$/, "");
  const host = await pseudonymousHost(data.windows.at(-1)?.host ?? "unknown");
  const body = JSON.stringify({
    schema_version: "1.0",
    host,
    observed_at: data.windows.at(-1)?.timestamp ?? new Date().toISOString(),
    window_count: data.windows.length,
    open_alerts: data.alerts.filter((item) => item.status === "open").length,
    critical_alerts: data.alerts.filter(
      (item) => item.status === "open" && item.severity === "critical",
    ).length,
    readiness_state: data.readiness.state,
    coverage_ratio: data.readiness.coverageRatio,
  });
  const response = await fetch(`${endpoint}/api/ingest`, {
    method: "POST",
    headers: { "content-type": "application/json", ...await signedHeaders(settings.apiKey, body, host) },
    body,
  });
  const value = await response.json() as JsonObject;
  if (!response.ok) throw new Error(String(value.error || `HTTP ${response.status}`));
  return String(value.id || "accepted");
}

export async function fetchSnapshots(settings: LiveSettings): Promise<AggregateSnapshot[]> {
  const endpoint = settings.endpoint.replace(/\/$/, "");
  const response = await fetch(`${endpoint}/api/snapshot`, {
    headers: { "x-ingest-key": settings.apiKey },
  });
  const value = await response.json() as JsonObject;
  if (!response.ok) throw new Error(String(value.error || `HTTP ${response.status}`));
  return Array.isArray(value.snapshots) ? value.snapshots as unknown as AggregateSnapshot[] : [];
}
